/**
 * A calendar date — the accreditation expiry, the next scheduled assessment.
 *
 * `type="date"` is used here, and deliberately not routed through `TextField`. The §9
 * prohibition names `number` and `email`, and its cause is specific: those types return
 * `null` from `selectionStart`, so an interface that *restores the caret on re-render*
 * puts it at 0 and the text types backwards. This component restores no caret at all —
 * the native control owns its own segmented cursor and moves it itself — so the defect
 * has nothing to attach to.
 *
 * The wire value is `YYYY-MM-DD`, which is what the native control emits and what the
 * server parses. An incomplete date yields an empty string, so a half-typed date reads as
 * absent rather than as a different day.
 */

export interface DateFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  error?: string;
  required?: boolean;
}

export function DateField({
  id,
  label,
  value,
  onChange,
  hint,
  error,
  required = false,
}: DateFieldProps) {
  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-error` : null]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="field">
      <label className="f" htmlFor={id}>
        {label} {required ? <b>*</b> : null}
        {hint ? <i id={`${id}-hint`}> {hint}</i> : null}
      </label>
      <input
        id={id}
        type="date"
        className="t"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
      />
      {error ? (
        <p className="err" id={`${id}-error`} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
